import React, { useLayoutEffect, useRef, useState } from 'react';

import { Button } from '#ui/button.js';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '#ui/dialog.js';
import { Textarea } from '#ui/textarea.js';

interface ItemNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialNotes?: string | null;
  onConfirm: (notes: string) => Promise<void>;
}

export function ItemNotesDialog({ open, onOpenChange, initialNotes, onConfirm }: ItemNotesDialogProps) {
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 224)}px`;
  });

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onConfirm(notes);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Note</DialogTitle>
        </DialogHeader>

        <Textarea
          ref={textareaRef}
          placeholder="Add a personal note for this item…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="resize-none overflow-y-auto"
          autoFocus
        />

        <DialogFooter className="sm:justify-between">
          {initialNotes ? (
            <Button
              variant="destructive"
              onClick={async () => {
                setIsSaving(true);
                try {
                  await onConfirm('');
                  onOpenChange(false);
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              Remove note
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
